import React, { Fragment } from 'react';
import {
  Typography,
  Grid,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import FileItem from '@/components/FunnelsComponents/Shared/FileItem';
import { map } from "lodash";
import { objectLength } from '@/utils/helperFunctions';
import { ACCEPTED_FILE_TYPES, ALLOWED_FILE_EXTENSIONS_TEXT } from '@/utils/constants';
import { UploadedFile } from '@/Contexts/Funnel/types';
import { blue, grey } from '@mui/material/colors';

interface FilesListProps {
  files: UploadedFile[];
  onFileRemove: (fileId: string) => void;
  onFileUpload: (uploadedFiles: File[]) => Promise<void>;
}

const FilesList: React.FC<FilesListProps> = ({ files, onFileRemove, onFileUpload }) => {
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    multiple: true,
    onDrop: onFileUpload,
    accept: ACCEPTED_FILE_TYPES,
  });

  return (
    <Grid
      container
      direction="column"
      sx={{ p: 2, height: "100%" }}
      data-testid="dropzone-wrapper"
      wrap="nowrap"
    >
      <Grid
        container
        {...getRootProps()}
        direction="column"
        alignItems="center"
        justifyContent={objectLength(files) ? "flex-start" : "center"}
        sx={{
          padding: 4,
          borderRadius: 1,
          cursor: "pointer",
          border: "2px dashed",
          transition: "all 0.2s ease",
          borderColor: isDragActive ? blue["400"] : grey["300"],
          backgroundColor: isDragActive ? blue["50"] : "transparent",
          overflowY: 'auto'
        }}
      >
        <input {...getInputProps()} />
        {!objectLength(files) ? (
          <Fragment>
            <Grid>
              <UploadFile sx={{ fontSize: 64, color: isDragActive ? "#2196f3" : grey["300"], mb: 2 }} />
            </Grid>
            <Grid>
              <Typography variant="h6" sx={{ color: isDragActive ? "#2196f3" : grey["600"], mb: 1 }}>
                Drop files here
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body2" sx={{ color: grey["600"] }}>
                or click 'Upload Files' to browse
              </Typography>
            </Grid>
          </Fragment>
        ) : (
          <Grid container direction="column" spacing={0} sx={{ width: '100%' }}>
            {map(files, (file) => (
              <Grid key={file.id} sx={{ width: '100%' }}>
                <FileItem
                  file={file}
                  onRemove={onFileRemove}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>

      {objectLength(fileRejections as unknown[]) > 0 && (
        <Grid
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336'
          }}
        >
          <Typography variant="body2" color="error" fontWeight="bold" gutterBottom>
            Invalid file type detected
          </Typography>
          <Typography variant="body2" color="error">
            Allowed file types: {ALLOWED_FILE_EXTENSIONS_TEXT}.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default FilesList;
